import { useQuery, type Query } from "@tanstack/react-query";
import { documentApiService } from "../services/documentApiService";

type PresignedUrlData = {
    url: string;
    expiresAt: number;
};

export function usePresignedUrl(fileKey: string, bufferTime = 300) {
    return useQuery<PresignedUrlData>({
        queryKey: ["presignedUrl", fileKey],
        queryFn: async () => {
            try {
                console.log(`[PresignedUrl] Requesting URL for ${fileKey}`);
                const response = await documentApiService.generateDownloadUrl(fileKey);
                const expiresAt = Date.now() + response.expiresIn * 1000;
                const expiresInMin = Math.round(response.expiresIn / 60);
                console.log(
                    `[PresignedUrl] Received URL for ${fileKey} (expires in ${expiresInMin} min)`
                );
                return {
                    url: response.url,
                    expiresAt,
                };
            } catch (error) {
                console.error(`[PresignedUrl] Failed to get URL for ${fileKey}:`, error);
                throw error; // Re-throw to let React Query handle the error state
            }
        },
        // Keep the data fresh by refetching before expiry
        refetchInterval: (query: Query<PresignedUrlData, Error>) => {
            if (!query.state.data) return false;
            // Calculate time until we need to refresh (expiry minus buffer)

            const timeUntilRefresh =
                query.state.data.expiresAt - Date.now() - bufferTime * 1000;

            if (timeUntilRefresh <= 0) {
                console.log(`[PresignedUrl] Refreshing URL for ${fileKey} (expired)`);
                return false;
            } else {
                // Only log when refresh is scheduled to happen soon
                if (timeUntilRefresh < 60000) {
                    // Less than a minute
                    console.log(
                        `[PresignedUrl] Scheduling refresh for ${fileKey} in ${Math.round(timeUntilRefresh / 1000)}s`
                    );
                } else if (timeUntilRefresh < 300000) {
                    // Less than 5 minutes
                    console.log(
                        `[PresignedUrl] Scheduling refresh for ${fileKey} in ${Math.round(timeUntilRefresh / 60000)}m`
                    );
                }
                return timeUntilRefresh;
            }
        },
        // Keep cached data while fetching new data
        staleTime: Infinity,
        // Cache the result for the duration of the session
        gcTime: Infinity,
    });
}
